import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaArrowLeft,  FaTimes, FaHistory } from 'react-icons/fa';
import AssetuserSidebar from '../components/AssetuserSidebar';
import { API_BASE_URL } from '../config/api';
import '../styles/AssetUserHistoryPage.css';

const AssetUserHistoryPage = () => {
  const navigate = useNavigate();
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [assignments, setAssignments] = useState([]);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [issuesMap, setIssuesMap] = useState({});


  const fetchMyAssignmentHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/asset-users/my-assignment-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setAssignments(response.data.assignments || []);
        // Fetch issues for each assignment
        if (response.data.assignments && response.data.assignments.length > 0) {
          fetchIssuesForAssignments(response.data.assignments, token);
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load assignment history');
      console.error('Error fetching assignment history:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user.role !== 'asset_user') {
      navigate('/');
      return;
    }
    fetchMyAssignmentHistory();
  }, [user.role, navigate, fetchMyAssignmentHistory]);

  const fetchIssuesForAssignments = async (asgnts, token) => {
    try {
      const issuePromises = asgnts.map(asgnmt => {
        // Skip if no assetId or assetId._id
        if (!asgnmt.assetId || !asgnmt.assetId._id) {
          return Promise.resolve({ assignmentId: asgnmt._id, count: 0 });
        }
        
        return axios.get(`${API_BASE_URL}/api/issues?assetId=${asgnmt.assetId._id}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
          .then(res => ({
            assignmentId: asgnmt._id,
            count: res.data.issues?.length || 0
          }))
          .catch(() => ({ assignmentId: asgnmt._id, count: 0 }));
      });
      
      const issueResults = await Promise.all(issuePromises);
      const issueMap = {};
      issueResults.forEach(result => {
        issueMap[result.assignmentId] = result.count;
      });
      setIssuesMap(issueMap);
    } catch (err) {
      console.error('Error fetching issues:', err);
    }
  };

  const calculateDays = (fromDate, toDate) => {
    if (!fromDate) return 0;
    const from = new Date(fromDate);
    const to = toDate ? new Date(toDate) : new Date();
    const days = Math.ceil((to - from) / (1000 * 60 * 60 * 24));
    return Math.max(0, days);
  };

  const calculateRemainingDays = (assignedDate, returnDate, requestedDuration) => {
    // If already returned/released, show 0
    if (returnDate) {
      return '0';
    }

    // If no assigned date, return N/A
    if (!assignedDate) {
      return 'N/A';
    }

    // Parse duration string with multiple formats (\"1_week\", \"3_weeks\", \"1_month\", etc.)
    const durationMap = {
      '1_day': 1,
      '1_week': 7,
      '2_weeks': 14,
      '3_weeks': 21,
      '1_month': 30,
      '2_months': 60,
      '3_months': 90,
      '6_months': 180,
      'one_day': 1,
      'one_week': 7,
      'two_weeks': 14,
      'three_weeks': 21,
      'one_month': 30,
      'two_months': 60,
      'three_months': 90,
      'six_months': 180
    };

    // If no duration specified, return N/A
    if (!requestedDuration) {
      return 'N/A';
    }

    const durationDays = durationMap[requestedDuration];
    if (!durationDays || durationDays === 0) {
      return 'N/A';
    }

    // Calculate expected release date
    const expectedReleaseDate = new Date(assignedDate);
    expectedReleaseDate.setDate(expectedReleaseDate.getDate() + durationDays);

    // Calculate days remaining from today to expected release date
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expectedReleaseDate.setHours(0, 0, 0, 0);
    
    const remainingMs = expectedReleaseDate - today;
    const remainingDays = Math.ceil(remainingMs / (1000 * 60 * 60 * 24));

    if (remainingDays < 0) {
      return 'Overdue';
    }
    
    return remainingDays === 0 ? 'Today' : `${remainingDays} days`;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDuration = (duration) => {
    if (!duration) return 'N/A';
    return duration
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getStatusBadge = (status) => {
    const badges = {
      'active': { label: 'Active', color: '#10b981' },
      'returned': { label: 'Released', color: '#8b5cf6' },
      'swapped': { label: 'Swapped', color: '#f59e0b' }
    };
    return badges[status] || { label: status, color: '#6b7280' };
  };

  const handleViewDetails = (assignment) => {
    setSelectedAssignment(assignment);
    setShowDetailsModal(true);
  };

  if (loading) {
    return (
      <div className="asset-user-history-container">
        <AssetuserSidebar user={user} activeTab="asset-history" />
        <div className="asset-user-history-content">
          <div className="loading">Loading your asset history...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="asset-user-history-container">
      <AssetuserSidebar user={user} activeTab="asset-history" />
      
      <div className="asset-user-history-content">
        {/* Header */}
        <div className="history-header">
          <button className="btn-back" onClick={() => navigate('/asset-user-dashboard')}>
            <FaArrowLeft /> Back to Dashboard
          </button>
          <div>
            <h1>My Asset History</h1>
            <p>View all your past and current asset assignments</p>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        {assignments.length === 0 ? (
          <div className="empty-state">
            <FaHistory className="empty-icon" />
            <h3>No assignment history yet</h3>
            <p>Once you are assigned assets, your history will appear here.</p>
          </div>
        ) : (
          <div className="assignments-table-wrapper">
            <table className="assignments-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Asset Name</th>
                  <th>Serial Number</th>
                  <th>Unit ID</th>
                  <th>Assigned Date</th>
                  <th> Usage Period</th>
                  <th>Remaining Days</th>
                  <th>Status</th>
                  <th>Issues</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {assignments.map((assignment, index) => {
                  const asset = assignment.assetId;
                  const statusBadge = getStatusBadge(assignment.status);
                  const issueCount = issuesMap[assignment._id] || 0;
                  
                  return (
                    <tr key={assignment._id}>
                      <td className="row-number">{index + 1}</td>
                      <td className="asset-name">{asset?.assetName || 'Unknown'}</td>
                      <td className="serial-number">{asset?.serialNumber || 'N/A'}</td>
                      <td className="unit-id">{assignment.unitId || 'N/A'}</td>
                      <td className="assigned-date">{formatDate(assignment.assignedDate)}</td>
                      <td className="duration">{calculateDays(assignment.assignedDate, assignment.returnDate)} days</td>
                      <td className="remaining-days">
                        <span style={{
                          color: calculateRemainingDays(assignment.assignedDate, assignment.returnDate, assignment.assetRequest?.duration) === 'Overdue' ? '#dc2626' 
                               : calculateRemainingDays(assignment.assignedDate, assignment.returnDate, assignment.assetRequest?.duration) === '0' ? '#8b5cf6'
                               : '#3b82f6',
                          fontWeight: '600'
                        }}>
                          {calculateRemainingDays(assignment.assignedDate, assignment.returnDate, assignment.assetRequest?.duration)}
                        </span>
                      </td>
                      <td className="status-cell">
                        <span 
                          className="status-badge"
                          style={{ backgroundColor: statusBadge.color }}
                        >
                          {statusBadge.label}
                        </span>
                      </td>
                      <td className={`issues-count ${issueCount > 0 ? 'has-issues' : ''}`}>
                        {issueCount}
                      </td>
                      <td className="actions-cell">
                        <button 
                          className="btn-view-details"
                          onClick={() => handleViewDetails(assignment)}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedAssignment && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Assignment Details</h2>
              <button className="modal-close" onClick={() => setShowDetailsModal(false)}>
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              <div className="detail-section">
                <h4>Asset Information</h4>
                <div className="detail-grid">
                  <div className="detail-row">
                    <span className="label">Asset Name:</span>
                    <span className="value">{selectedAssignment.assetId?.assetName || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Serial Number:</span>
                    <span className="value">{selectedAssignment.assetId?.serialNumber || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Category:</span>
                    <span className="value">{selectedAssignment.assetId?.category || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Brand/Model:</span>
                    <span className="value">
                      {[selectedAssignment.assetId?.brand, selectedAssignment.assetId?.model]
                        .filter(Boolean)
                        .join(' / ') || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              {selectedAssignment.assetId?.specifications && Object.keys(selectedAssignment.assetId.specifications).length > 0 && (
                <div className="detail-section">
                  <h4>Specifications</h4>
                  <div className="detail-grid">
                    {Object.entries(selectedAssignment.assetId.specifications).map(([key, value]) => (
                      <div className="detail-row" key={key}>
                        <span className="label">{key}:</span>
                        <span className="value">{value || 'N/A'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="detail-section">
                <h4>Allocation Timeline</h4>
                <div className="detail-grid">
                  <div className="detail-row">
                    <span className="label">Unit ID:</span>
                    <span className="value">{selectedAssignment.unitId || 'Not specified'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Assigned Date:</span>
                    <span className="value">{formatDate(selectedAssignment.assignedDate)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Request Date:</span>
                    <span className="value">
                      {formatDate(selectedAssignment.assetRequest?.createdAt || selectedAssignment.assetRequest?.requestedAt)}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Expected Duration:</span>
                    <span className="value">{formatDuration(selectedAssignment.assetRequest?.duration)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Allocated Till:</span>
                    <div className="allocation-info">
                      <span className="value" style={{ display: 'block', marginBottom: '0.5rem' }}>
                        {selectedAssignment.assignedDate && selectedAssignment.assetRequest?.duration
                          ? (() => {
                              const durationMap = {
                                '1_day': 1, '1_week': 7, '2_weeks': 14, '3_weeks': 21,
                                '1_month': 30, '2_months': 60, '3_months': 90, '6_months': 180,
                                'one_day': 1, 'one_week': 7, 'two_weeks': 14, 'three_weeks': 21,
                                'one_month': 30, 'two_months': 60, 'three_months': 90, 'six_months': 180
                              };
                              const durationDays = durationMap[selectedAssignment.assetRequest.duration];
                              const expectedDate = new Date(selectedAssignment.assignedDate);
                              expectedDate.setDate(expectedDate.getDate() + durationDays);
                              return formatDate(expectedDate);
                            })()
                          : 'N/A'}
                      </span>
                      <span className="remaining-info">
                        {calculateRemainingDays(selectedAssignment.assignedDate, selectedAssignment.returnDate, selectedAssignment.assetRequest?.duration)}
                      </span>
                    </div>
                  </div>
                  {selectedAssignment.returnDate && (
                    <>
                      <div className="detail-row">
                        <span className="label">Released Date:</span>
                        <span className="value">{formatDate(selectedAssignment.returnDate)}</span>
                      </div>
                      <div className="detail-row">
                        <span className="label">Total Duration:</span>
                        <span className="value">
                          {calculateDays(selectedAssignment.assignedDate, selectedAssignment.returnDate)} days
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="detail-section">
                <h4>Status & Issues</h4>
                <div className="detail-grid">
                  <div className="detail-row">
                    <span className="label">Assignment Status:</span>
                    <span 
                      className="value status-badge-inline"
                      style={{ backgroundColor: getStatusBadge(selectedAssignment.status).color }}
                    >
                      {getStatusBadge(selectedAssignment.status).label}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Issues Reported:</span>
                    <span className="value">
                      {issuesMap[selectedAssignment._id] || 0} issue(s)
                    </span>
                  </div>
                  {selectedAssignment.remarks && (
                    <div className="detail-row full-width">
                      <span className="label">Remarks:</span>
                      <p className="remarks-text">{selectedAssignment.remarks}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-close" onClick={() => setShowDetailsModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetUserHistoryPage;
